"use client";

import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import { Header } from "@/components/header";
import dynamic from "next/dynamic";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useInView } from "@/hooks/useInView";
import { useLanguage } from "@/context/LanguageContext";
import { clientsApi, cardsApi, contentInfoApi, blogsApi, researchPapersApi } from "@/lib/api";
import { getImageUrl } from "@/lib/utils";

// Lazy load Footer and ChatWidget - not critical for initial render
const Footer = dynamic(() => import("@/components/footer").then(mod => ({ default: mod.Footer })), {
  loading: () => <div className="h-75 bg-gray-200 animate-pulse" />,
});

const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

interface Client {
  id_client: string;
  name: string;
  logo: string;
  website: string;
  industry: string;
  is_active: boolean;
}

interface Card {
  id_card: string;
  card_title: string;
  card_description: string;
  logo: string;
  display_order: number;
}

interface ContentInfo {
  id_content: string;
  content_title: string;
  content_description: string;
  type: string;
}

interface BlogPreview {
  id_blog: string;
  title: string;
  slug?: string;
  header_img?: string | null;
  abstract?: string | null;
  published_at?: string | null;
  tags?: string | null;
}

interface ResearchPreview {
  id: string;
  title: string;
  abstract?: string;
  published_at?: string;
  author?: string;
  research_url?: string;
}

export default function Home() {
  const { language } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [ganeshaitCurrentIndex, setGaneshaitCurrentIndex] = useState(0);
  const [isGaneshaitAnimating, setIsGaneshaitAnimating] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number>(1920);

  const labels = {
    en: {
      contactUs: "Contact Us",
      seeOurTeam: "See Our Team",
    },
    id: {
      contactUs: "Hubungi Kami",
      seeOurTeam: "Lihat Tim Kami",
    },
  };

  const t = labels[language];

  // API Data States - start empty, populate on fetch
  const [partners, setPartners] = useState<Client[]>([]);
  const [ganeshaitCards, setGaneshaitCards] = useState<Card[]>([]);
  const [heroContent, setHeroContent] = useState<Record<string, ContentInfo>>({});
  const [latestBlogs, setLatestBlogs] = useState<BlogPreview[]>([]);
  const [latestResearch, setLatestResearch] = useState<ResearchPreview | null>(null);
  const [latestResearchList, setLatestResearchList] = useState<ResearchPreview[]>([]);
  const [fetchError, setFetchError] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  // Mobile blog carousel state: show 1 card on small screens with arrow navigation
  const [blogIndex, setBlogIndex] = useState(0);

  // Helper function to render title with logo if it contains MarkAny or GaneshaIT
  // Memoized for performance
  const renderTitleWithLogo = useCallback((title: string) => {
    if (!title) return "";

    const titleLower = title.toLowerCase();

    if (titleLower.includes("markany")) {
      const parts = title.split(/markany/i);
      const before = (parts[0] || "").trim(); // "From"
      const after = (parts[1] || "").trim();  // "* to Indonesia" or "to Indonesia"

      return (
        <span className="inline-flex flex-col items-center text-center leading-tight sm:flex-row sm:items-center sm:text-left sm:gap-3 ">
          {/* Left: "From" + logo */}
          <span className="inline-flex items-center justify-center gap-2">
            {before && <span className="whitespace-nowrap">{before}</span>}

            {/* <span className="relative inline-block shrink-0 h-8 w-[120px] sm:h-9 sm:w-[150px] md:h-11 md:w-55"> */}
            <span className="relative inline-block shrink-0 h-9 w-35 -mb-0.5 md:-mb-2.5 ms:-mx-2.5 sm:h-10 sm:w-42.5 md:h-12 md:w-55">
              {/* <span className="relative inline-block shrink-0 h-9 w-35 sm:h-10 sm:w-42.5 md:h-12 md:w-[260px]"> */}
              <Image
                src="/homepage/homepage-picture/markany.svg"
                alt="MarkAny"
                fill
                className="object-contain"
                priority
                sizes="(max-width: 640px) 120px, (max-width: 768px) 150px, 220px"
              />
            </span>
          </span>

          {/* Right: "to Indonesia" */}
          {after && (
            <span className="mt-2 md:-mx-1.5 sm:mt-0 sm:inline">
              {after}
            </span>
          )}
        </span>
      );
    }

    if (titleLower.includes("ganeshait")) {
      const parts = title.split(/ganeshait/i);

      return (
        <>
          {parts[0]}
          <span className="relative inline-block h-10 w-56 sm:h-11 sm:w-64 md:h-13 md:w-76 lg:h-15 lg:w-88 shrink-0 mx-4 sm:mx-5">
            <Image
              src="/homepage/homepage-picture/ganeshait.svg"
              alt="GaneshaIT"
              fill
              className="object-contain"
              priority
              sizes="(max-width: 640px) 200px, (max-width: 768px) 220px, (max-width: 1024px) 260px, 300px"
            />
          </span>
          {parts[1]}
        </>
      );
    }

    // If neither keyword found, return title as-is
    return title;
  }, []);


  // Scroll reveal hooks for sections
  const [markanyRef, markanyInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [trustedRef, trustedInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [ganeshaitRef, ganeshaitInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

  // Memoize duplicated partners array to avoid recreation
  const duplicatedPartners = useMemo(() =>
    partners.length > 0 ? [...partners, ...partners] : [],
    [partners]
  );

  // Fetch data from API - Optimized for fast initial render
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        // Step 1: Fetch hero content FIRST for instant rendering
        const contentResponse = await contentInfoApi.getAll({ limit: 20, lang: language });

        if (!isMounted) return;

        // Set hero content immediately for fast visual feedback
        if (contentResponse.data && contentResponse.data.length > 0) {
          const contentMap: Record<string, ContentInfo> = {};
          contentResponse.data.forEach((item: ContentInfo) => {
            if (item.type) {
              contentMap[item.type] = item;
            }
          });
          setHeroContent(contentMap);
          setHeroLoaded(true);
        } else {
          setHeroLoaded(true); // Still set loaded even if empty
        }

        // Step 2: Fetch other data in parallel (doesn't block hero)
        const [clientsResponse, cardsResponse, blogsResponse, researchResponse] = await Promise.all([
          clientsApi.getAll({ is_active: true, lang: language }),
          cardsApi.getAll({ limit: 10, lang: language }),
          blogsApi.getAll({ limit: 3, is_active: true, lang: language }),
          researchPapersApi.getAll({ limit: 6, is_published: true, lang: language }),
        ]);

        if (!isMounted) return;

        // Set clients/partners
        if (clientsResponse.data && clientsResponse.data.length > 0) {
          setPartners(clientsResponse.data);
        }

        // Set cards (sorted by display_order)
        if (cardsResponse.data && cardsResponse.data.length > 0) {
          const sortedCards = cardsResponse.data.sort((a: Card, b: Card) => a.display_order - b.display_order);
          setGaneshaitCards(sortedCards);
        }

        // Set latest blogs (top 3) and reset mobile carousel index
        const blogsDataCandidate = blogsResponse.data?.data || blogsResponse.data;
        if (Array.isArray(blogsDataCandidate)) {
          const blogsData = blogsDataCandidate as BlogPreview[];
          if (blogsData.length > 0) {
            setLatestBlogs(blogsData.slice(0, 3));
            setBlogIndex(0);
          } else {
            setLatestBlogs([]);
            setBlogIndex(0);
          }
        }

        // Set latest research list (top N) - map safely to strings
        const researchDataCandidate = researchResponse.data?.data || researchResponse.data;
        if (Array.isArray(researchDataCandidate) && researchDataCandidate.length > 0) {
          type ResearchApiItem = {
            id?: string | number;
            title?: string;
            abstract?: string;
            published_at?: string;
            author?: string;
            team?: { id_team?: string; name_team?: string } | null;
            research_url?: string;
          };
          const mapped = (researchDataCandidate as ResearchApiItem[]).map((r, idx) => {
            const authorString =
              typeof r.author === "string" && r.author
                ? r.author
                : r.team && r.team.name_team
                  ? r.team.name_team
                  : "";

            const safeId =
              r.id != null && String(r.id).trim() !== ""
                ? String(r.id)
                : `research-${idx}`;

            return {
              id: safeId,
              title: r.title || "",
              abstract: r.abstract || "",
              published_at: r.published_at || "",
              author: authorString,
              research_url: r.research_url || "",
            } as ResearchPreview;
          });


          setLatestResearchList(mapped);
          // pick first as featured
          const first = mapped[0];
          if (first) setLatestResearch(first);
        }

        // Check if we got any data at all
        const hasAnyData =
          (clientsResponse.data && clientsResponse.data.length > 0) ||
          (cardsResponse.data && cardsResponse.data.length > 0) ||
          (contentResponse.data && contentResponse.data.length > 0) ||
          (Array.isArray(blogsResponse.data?.data || blogsResponse.data) && (blogsResponse.data?.data || blogsResponse.data).length > 0) ||
          (Array.isArray(researchResponse.data?.data || researchResponse.data) && (researchResponse.data?.data || researchResponse.data).length > 0);

        setFetchError(!hasAnyData);
      } catch (err) {
        console.log('Error fetching homepage data:', err);
        if (isMounted) {
          setFetchError(true);
          setHeroLoaded(true); // Still show page even on error
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [language]);

  useEffect(() => {
    // Handle scroll for hero background change with requestAnimationFrame for smoother performance
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          setScrolled(scrollPosition > 100);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Set initial window width after hydration to prevent mismatch
  // This setState is necessary for client-side hydration to work correctly
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setWindowWidth(window.innerWidth);
  }, []);

  // Handle window resize for responsive carousel
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // GaneshaIT carousel navigation functions (memoized)
  const ganeshaitNextSlide = useCallback(() => {
    if (isGaneshaitAnimating) return;
    setIsGaneshaitAnimating(true);
    setGaneshaitCurrentIndex((prevIndex) =>
      prevIndex === ganeshaitCards.length - 1 ? 0 : prevIndex + 1
    );
  }, [isGaneshaitAnimating, ganeshaitCards.length]);

  const ganeshaitPrevSlide = useCallback(() => {
    if (isGaneshaitAnimating) return;
    setIsGaneshaitAnimating(true);
    setGaneshaitCurrentIndex((prevIndex) =>
      prevIndex === 0 ? ganeshaitCards.length - 1 : prevIndex - 1
    );
  }, [isGaneshaitAnimating, ganeshaitCards.length]);

  const ganeshaitGoToSlide = useCallback((index: number) => {
    if (isGaneshaitAnimating || index === ganeshaitCurrentIndex) return;
    setIsGaneshaitAnimating(true);
    setGaneshaitCurrentIndex(index);
  }, [isGaneshaitAnimating, ganeshaitCurrentIndex]);

  // Other research list (exclude featured `latestResearch` and limit to 5)
  const otherResearchList = useMemo(() => {
    if (!latestResearchList || latestResearchList.length === 0) return [] as ResearchPreview[];
    const filtered = latestResearchList.filter((r) => r.id !== (latestResearch?.id || ''));
    // If research less than or equal 5, show all; otherwise limit to 5
    return filtered.slice(0, 5);
  }, [latestResearchList, latestResearch]);
  const blogNext = useCallback(() => {
    if (!latestBlogs || latestBlogs.length === 0) return;
    setBlogIndex((i) => (i + 1) % latestBlogs.length);
  }, [latestBlogs]);

  const blogPrev = useCallback(() => {
    if (!latestBlogs || latestBlogs.length === 0) return;
    setBlogIndex((i) => (i - 1 + latestBlogs.length) % latestBlogs.length);
  }, [latestBlogs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsGaneshaitAnimating(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [ganeshaitCurrentIndex]);

  return (
    <div className="page-layout">
      <div className="page-content">
        {/* Hero Section with Dark Blue Background */}
        <section
          className="relative overflow-hidden min-h-screen pt-20"
          style={{
            color: scrolled ? '#285494' : 'white',
            transition: 'color 1.8s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Gradient Background Layer - always present */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, #0C1A2E 30%, #285494 70%, #285494 100%)',
              zIndex: 0,
            }}
          />

          {/* White Background Layer - fades in on scroll */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'white', opacity: scrolled ? 1 : 0, transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1)', zIndex: 1, }}
          />

          {/* Animated Stars */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 2 }}>
            {/* Star 1 - Show on all devices */}
            <div
              className="absolute top-[10%] left-[5%] opacity-30"
              style={{
                animation: 'float 4s ease-in-out infinite',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
                transition: 'filter 1.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'filter, transform',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={50} height={50} loading="lazy" />
            </div>

            {/* Star 2 - Show on all devices */}
            <div
              className="absolute top-[8%] right-[2%] opacity-40"
              style={{
                animation: 'float 6s ease-in-out infinite 1s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
                transition: 'filter 1.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'filter, transform',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={70} height={70} loading="lazy" />
            </div>

            {/* Star 3 - Hide on mobile */}
            <div
              className="absolute top-[60%] left-[10%] opacity-35 hidden md:block"
              style={{
                animation: 'float 5s ease-in-out infinite 2s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
                transition: 'filter 1.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'filter, transform',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={60} height={60} loading="lazy" />
            </div>

            {/* Star 4 - Show on all devices done */}
            <div
              className="absolute bottom-[15%] right-[20%] opacity-30 "
              style={{
                animation: 'float 7s ease-in-out infinite 1.5s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
                transition: 'filter 1.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'filter, transform',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={55} height={55} loading="lazy" />
            </div>

            {/* Star 5 - Show on all devices */}
            {windowWidth > 768 && (
              <div
                className="absolute top-[32%] left-[12%] opacity-25 "
                style={{
                  animation: 'float 3s ease-in-out infinite 0.5s',
                  filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
                }}
              >
                <Image src="/homepage/homepage-picture/star.svg" alt="" width={45} height={45} loading="lazy" />
              </div>
            )}


            {/* Star 6 - Hide on mobile */}
            <div
              className="absolute top-[70%] right-[8%] opacity-40 hidden md:block"
              style={{
                animation: 'float 8s ease-in-out infinite 3s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={65} height={65} loading="lazy" />
            </div>

            {/* Star 7 - Hide on mobile */}
            {windowWidth > 768 && (
              <div
                className="absolute top-[15%] left-[35%] opacity-20 hidden md:block"
                style={{
                  animation: 'float 5s ease-in-out infinite 2.5s',
                  filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
                }}
              >
                <Image src="/homepage/homepage-picture/star.svg" alt="" width={40} height={40} loading="lazy" />
              </div>
            )}

            {/* Star 8 - Show on all devices */}
            {windowWidth > 768 && (
              <div
                className="absolute top-[50%] right-[30%] opacity-35 "
                style={{
                  animation: 'float 7s ease-in-out infinite 1.2s',
                  filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
                }}
              >
                <Image src="/homepage/homepage-picture/star.svg" alt="" width={58} height={58} loading="lazy" />
              </div>
            )}

            {/* Star 9 - Hide on mobile */}
            <div
              className="absolute bottom-[25%] left-[15%] opacity-28 hidden md:block"
              style={{
                animation: 'float 4s ease-in-out infinite 3.5s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={28} height={28} loading="lazy" />
            </div>

            {/* Star 10 - Show on all devices */}
            {windowWidth > 768 && (
              <div
                className="absolute top-[35%] right-[10%] opacity-30 "
                style={{
                  animation: 'float 6s ease-in-out infinite 1.8s',
                  filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
                }}
              >
                <Image src="/homepage/homepage-picture/star.svg" alt="" width={52} height={52} loading="lazy" />
              </div>
            )}

            {/* Star 11 - Hide on mobile */}
            <div
              className="absolute top-[80%] left-[30%] opacity-25 hidden md:block"
              style={{
                animation: 'float 3s ease-in-out infinite 4s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={42} height={42} loading="lazy" />
            </div>

            {/* Star 12 - Hide on mobile */}
            <div
              className="absolute bottom-[35%] right-[5%] opacity-38 hidden md:block"
              style={{
                animation: 'float 8s ease-in-out infinite 2.8s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={62} height={62} loading="lazy" />
            </div>

            {/* Star 13 - Hide on mobile */}
            <div
              className="absolute top-[5%] left-[45%] opacity-22 hidden md:block"
              style={{
                animation: 'float 5s ease-in-out infinite 1.8s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={46} height={46} loading="lazy" />
            </div>

            {/* Star 14 - Hide on mobile */}
            <div
              className="absolute top-[55%] left-[40%] opacity-20 hidden md:block"
              style={{
                animation: 'float 4s ease-in-out infinite 3.2s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={38} height={38} loading="lazy" />
            </div>

            {/* Star 15 - Show on all devices (no - awa test)*/}
            <div
              className="absolute bottom-[10%] left-[5%] opacity-32 "
              style={{
                animation: 'float 7s ease-in-out infinite 2.3s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={56} height={56} loading="lazy" />
            </div>

            {/* Star 16 - Hide on mobile */}
            <div
              className="absolute top-[25%] right-[35%] opacity-26 hidden md:block"
              style={{
                animation: 'float 5s ease-in-out infinite 1.6s',
                filter: scrolled ? 'brightness(0) saturate(100%) invert(17%) sepia(97%) saturate(2764%) hue-rotate(201deg) brightness(91%) contrast(101%)' : 'none',
              }}
            >
              <Image src="/homepage/homepage-picture/star.svg" alt="" width={44} height={44} loading="lazy" />
            </div>

          </div>

          {/* Navigation Bar */}
          <Header activePage="home" />

          {/* Main Content Section */}
          <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-120px)] px-8 lg:px-16 text-center">
            {/* Headline */}
            {/* <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-8 max-w-5xl leading-tight">
            Solution We Design, Keep Your Business Align
          </h1> */}

            <div
              className={`mb-4 flex justify-center transition-opacity duration-700 ${heroLoaded && heroContent['hero-1'] ? 'opacity-100' : 'opacity-0'
                }`}
            >
              <span className="relative mb-4 inline-block h-10 w-56 md:h-16 md:w-80 lg:h-20 lg:w-100">
                <Image
                  src="/homepage/homepage-picture/ganeshait-outline.svg"
                  alt="GaneshaIT"
                  fill
                  className="object-contain"
                  priority
                  sizes="(max-width: 768px) 224px, (max-width: 1024px) 320px, 400px"
                />
              </span>
            </div>

            <h1
              className={`text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-8 max-w-5xl leading-tight ${scrolled ? 'text-[#285494]' : 'text-white'} ${heroLoaded && heroContent['hero-1'] ? 'opacity-100' : 'opacity-0'} transition-all duration-400 flex flex-wrap items-center justify-center gap-2`}
              style={{
                transition: 'color 1.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s ease-in-out',
              }}
            >
              {heroContent['hero-1']?.content_title
                ? renderTitleWithLogo(heroContent['hero-1'].content_title)
                : (fetchError ? 'Solution We Design, Keep Your Business Align' : '')
              }
            </h1>

            {/* Description Paragraph */}
            <p
              className={`text-base md:text-lg lg:text-xl mb-12 max-w-4xl leading-relaxed ${scrolled ? 'text-[#000000]' : 'text-white'} ${heroLoaded && heroContent['hero-1'] ? 'opacity-90' : 'opacity-0'} transition-all duration-400`}
              style={{
                transition: 'color 1.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s ease-in-out',
              }}
            >
              {heroContent['hero-1']?.content_description || (fetchError ? 'GaneshaIT furnishes enterprise-grade technology solutions designed for flawless execution and the unwavering reliability of your critical business assets. We deploy cutting-edge, proprietary software and sophisticated integration platforms to establish a robust framework, ensuring the comprehensive integrity and continuous protection of your organizational data.' : '')}
            </p>

            {/* Contact Us Button */}
            <Link href="/contact">
              <Button
                className={`
                px-6
                py-6
                text-xl
                font-semibold
                text-[#285494]
                rounded-[10px]
                border-2
                border-transparent
                bg-white
                transition-all
                hover:bg-blue-50
                ${heroLoaded && heroContent['hero-1'] ? 'opacity-100' : 'opacity-0'}
                duration-400
              `}
                style={{
                  background:
                    "linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #B8CEED 0%, #2A66BE 100%) border-box",
                  transition: 'opacity 0.6s ease-in-out 0.2s',
                }}
              >
                {t.contactUs}
              </Button>
            </Link>

          </main>
        </section>

        {/* From MarkAny* to Indonesia Section */}
        <section className="bg-white pt-20 pb-3 px-8 lg:px-16">
          <div ref={markanyRef} className="max-w-4xl mx-auto text-center">
            <h2
              className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 reveal text-[#0C2E6C]
            ${markanyInView ? "is-visible reveal-delay-100" : ""}
            ${heroLoaded && heroContent["hero-2"] ? "opacity-100" : "opacity-0"}
            transition-opacity duration-400`}
            >
              {heroContent["hero-2"]?.content_title ? (
                renderTitleWithLogo(heroContent["hero-2"].content_title)
              ) : fetchError ? (
                <span className="flex flex-col items-center text-center leading-tight">
                  {/* Line 1 */}
                  <span className="flex items-center justify-center gap-2">
                    <span>From</span>

                    {/* Logo */}
                    <span className="relative inline-block h-9 w-35 sm:h-10 sm:w-42.5 md:h-12 md:w-60">
                      <Image
                        src="/homepage/homepage-picture/markany.svg"
                        alt="MarkAny"
                        fill
                        className="object-contain"
                        priority
                        sizes="(max-width: 640px) 140px, (max-width: 768px) 170px, 240px"
                      />
                    </span>
                  </span>

                  {/* Line 2 */}
                  <span className="mt-1">to Indonesia</span>
                </span>
              ) : (
                ""
              )}
            </h2>

            <p className={`text-[#000000] text-base md:text-lg mb-8 leading-relaxed max-w-3xl mx-auto reveal ${markanyInView ? 'is-visible reveal-delay-200' : ''} ${heroLoaded && heroContent['hero-2'] ? 'opacity-100' : 'opacity-0'} transition-opacity duration-400`}>
              {heroContent['hero-2']?.content_description || (fetchError ? 'GaneshaIT was born from MarkAny, a leading cybersecurity company from South Korea that has been trusted by various large companies, including Samsung and LG. GaneshaIT consists of 100% of Indonesia\'s best talent to deliver high-quality digital and data security solutions.' : '')}
            </p>

            <div className={`reveal ${markanyInView ? 'is-visible reveal-delay-300' : ''}`}>
              <Link href="/team">
                <Button
                  className="rounded-[10px] px-6 py-6 text-white font-semibold text-lg transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)" }}
                >
                  <span>{t.seeOurTeam}</span>

                  {/* <Image src="/homepage/homepage-picture/arrow-right.svg"
                    alt="Arrow"
                    width={24}
                    height={24}
                    className="w-6 h-6"
                    loading="lazy" /> */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Button>
              </Link>
            </div>

          </div>
        </section>


        {/* Trusted by Everyone Section */}
        <section className="bg-white py-20 px-8 lg:px-16">
          <div ref={trustedRef} className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2
                className={`
                text-xl
                sm:text-2xl
                md:text-2xl
                lg:text-3xl
                font-bold
                text-[#0C2E6C]
                mb-6
                text-center md:text-center
                reveal
                flex flex-wrap items-center justify-center gap-2
                ${trustedInView ? "is-visible reveal-delay-100" : ""}
                ${heroLoaded && heroContent['hero-3'] ? 'opacity-100' : 'opacity-0'}
              `}
              >
                {heroContent['hero-3']?.content_title
                  ? renderTitleWithLogo(heroContent['hero-3'].content_title)
                  : (fetchError ? 'Trusted by Everyone' : '')
                }
              </h2>

              <p className={`text-[#000000] text-base md:text-lg max-w-3xl mx-auto leading-relaxed reveal ${trustedInView ? 'is-visible reveal-delay-200' : ''} ${heroLoaded && heroContent['hero-3'] ? 'opacity-100' : 'opacity-0'} transition-opacity duration-400`}>
                {heroContent['hero-3']?.content_description || (fetchError ? 'GaneshaIT follows various high international standard certifications and institutions in providing product services, systems, and values.' : '')}
              </p>
            </div>

            {/* Partner Cards Carousel */}
            <div className={`partners-carousel reveal ${trustedInView ? 'is-visible reveal-delay-300' : ''} ${partners.length > 0 ? 'opacity-100' : 'opacity-0 h-0'} transition-all duration-400`}>
              <div className="partners-track flex gap-6 my-2">
                {duplicatedPartners.map((partner: Client, index: number) => (
                  <div
                    key={`partner-${index}`}
                    className="partner-card bg-white rounded-lg shadow-md p-6 flex flex-col items-center text-center hover:shadow-lg transition-shadow min-w-55 md:min-w-45"
                  >
                    {/* Logo section - fixed height for consistent alignment */}
                    <div className="mb-4 flex justify-center items-center h-15 w-full shrink-0">
                      <Image src={partner.logo}
                        alt={partner.name}
                        width={60}
                        height={60}
                        className="object-contain"
                        style={{ width: 'auto', height: 'auto' }}
                        unoptimized
                        loading="lazy" />
                    </div>

                    {/* Title section - flex-grow allows wrapping, min-height ensures alignment */}
                    <div className="mb-1 grow flex items-start justify-center w-full min-h-12">
                      <h3 className="font-bold text-[#0C2E6C] text-lg text-center wrap-break-word leading-tight w-full">
                        {partner.name}
                      </h3>
                    </div>

                    {/* Industry section - fixed at bottom, consistent alignment */}
                    <p className="text-gray-500 text-sm shrink-0">{partner.industry}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Latest Research Section (blue background) */}
        <section className="relative py-18 px-6 sm:px-8 lg:px-16 overflow-hidden">
          {/* Background */}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg,#0C2E6C 0%, #0C1A2E 100%)" }}
          />
          {/* Decorative blobs */}
          <div className="absolute -top-28 right-10 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-28 left-10 h-80 w-80 rounded-full bg-white/10 blur-3xl" />

          <div className="relative max-w-6xl mx-auto text-white">
            {/* Header row */}
            <div className="flex items-end justify-between gap-6 mb-8">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/70">Featured</p>
                <h3 className="mt-2 text-3xl md:text-4xl font-bold">Latest Research</h3>
              </div>

              <a
                href="/research"
                className="hidden sm:inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3
                   border border-white/30 text-white font-semibold hover:bg-white/10 transition"
              >
                See More
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>

            {/* Content */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-stretch">
              {/* Left (text) */}
              <div
                className="md:col-span-3 relative rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl
             p-6 pb-24 shadow-[0_10px_30px_rgba(0,0,0,0.18)]"
              >

                <h4 className="text-2xl md:text-3xl font-bold leading-tight">
                  {latestResearch?.title || "Read our most recent research and findings."}
                </h4>

                <p className="mt-4 text-white/85 leading-relaxed">
                  {latestResearch?.abstract
                    ? latestResearch.abstract.slice(0, 320) +
                    (latestResearch.abstract.length > 320 ? "..." : "")
                    : "We publish research that bridges security, data, and enterprise engineering—focused on real-world impact."}
                </p>

                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/75">
                  {latestResearch?.published_at && (
                    <span>
                      {new Date(latestResearch.published_at).toLocaleDateString(
                        language === "id" ? "id-ID" : "en-US",
                        { year: "numeric", month: "long", day: "numeric" }
                      )}
                    </span>
                  )}
                  {latestResearch?.author && <span>By {latestResearch.author}</span>}
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={latestResearch?.research_url || "#"}
                    className="absolute bottom-6 left-6 inline-flex items-center gap-2
             rounded-xl px-6 py-3 bg-white text-[#0C2E6C]
             font-semibold hover:opacity-95 transition"
                  >
                    Read Paper
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a>



                  {/* <a
                    href="/research"
                    className="inline-flex sm:hidden items-center justify-center gap-2 rounded-xl px-5 py-3
                       border border-white/30 text-white font-semibold hover:bg-white/10 transition"
                  >
                    See More
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a> */}
                </div>
              </div>

              {/* Right (visual / meta) */}
              <div
                className="md:col-span-2 rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl p-6
             shadow-[0_10px_30px_rgba(0,0,0,0.18)] hidden md:flex md:flex-col"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm text-white/90 font-semibold">Other Research</p>
                    <p className="mt-1 text-sm text-white/70 leading-relaxed">
                      A highlighted publication curated from our latest work security, systems, and enterprise grade engineering.
                    </p>
                  </div>

                  {/* <a
                    href="/research"
                    className="shrink-0 inline-flex items-center gap-2 rounded-xl px-3 py-2
                 border border-white/20 bg-white/5 text-white/90 text-xs font-semibold
                 hover:bg-white/10 transition"
                  >
                    View all
                    <span className="transition-transform hover:translate-x-0.5">→</span>
                  </a> */}
                </div>

                {/* Scrollable list */}
                <div className="relative mt-5 flex-1">
                  {/* Top & bottom fade masks */}
                  <div className="pointer-events-none absolute top-0 left-0 right-0 h-6" />
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6" />

                  <div
                    className="max-h-64 overflow-y-auto space-y-3 pr-2
                 scrollbar-thin scrollbar-thumb-white/25 scrollbar-track-transparent"
                  >
                    {otherResearchList && otherResearchList.length > 0 ? (
                      otherResearchList.map((r, idx) => {
                        const dateStr = r.published_at
                          ? new Date(r.published_at).toLocaleDateString(
                            language === "id" ? "id-ID" : "en-US",
                            { year: "numeric", month: "short", day: "numeric" }
                          )
                          : null;

                        return (
                          <a
                            key={r.id ? `research-${r.id}` : `research-${idx}`}
                            href={`/research/${r.id}`}
                            className="group block rounded-xl border border-white/15 bg-white/5
                         px-4 py-4 hover:bg-white/10 hover:border-white/25
                         transition"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-white leading-snug line-clamp-2">
                                  {r.title}
                                </p>
                                {dateStr && (
                                  <p className="mt-1 text-xs text-white/60">
                                    {dateStr}
                                  </p>
                                )}
                              </div>

                              {/* Chevron */}
                              <div className="mt-1 shrink-0 text-white/60 group-hover:text-white/90 transition">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M9 18l6-6-6-6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>
                          </a>
                        );
                      })
                    ) : (
                      <p className="text-sm text-white/60">
                        No research available.
                      </p>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* Latest Blogs Section (white background) */}
        <section className="relative py-16 px-6 sm:px-8 lg:px-16 overflow-hidden">
          {/* Background */}
          <div
            className="absolute inset-0"
            style={{ background: 'white' }}
          />
          {/* Decorative blobs */}
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

          <div className="relative max-w-6xl mx-auto text-black mb-12">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-15 mt-12">
              <div className="max-w-2xl">
                <p className="text-xs uppercase tracking-widest text-black/70">Latest Updates</p>
                <h3 className="mt-2 text-3xl md:text-4xl font-bold text-[#0C2E6C]">Latest Blog</h3>
                <p className="mt-2 text-black/80">
                  Latest insights and articles from GaneshaIT.
                </p>
              </div>

              {/* <Link
                href="/blogs"
                className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3
                   bg-white text-[#0C2E6C] font-semibold shadow-sm hover:opacity-95
                   transition"
              >
                See More
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link> */}

              <Button
                asChild
                className="rounded-[10px] px-6 py-6 text-white font-semibold text-lg
             transition-opacity hover:opacity-90 flex items-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                }}
              >
                <Link href="/blogs">
                  <span>See more blogs</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {/* <Image
                    src="/homepage/homepage-picture/arrow-right.svg"
                    alt="Arrow"
                    width={24}
                    height={24}
                    className="w-6 h-6"
                    loading="lazy"
                  /> */}
                </Link>
              </Button>


            </div>

            {/* Cards */}
            <div className="relative">
              {/* Mobile: single-card carousel with arrows */}
              {windowWidth <= 768 ? (
                <div className="flex items-center gap-4">
                  <button
                    aria-label="Previous blog"
                    onClick={blogPrev}
                    className="p-2 rounded-md bg-white/10 hover:bg-white/20 text-[#0C2E6C]"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  <div className="flex-1 min-h-105">
                    {latestBlogs && latestBlogs.length > 0 ? (
                      (() => {
                        const blog = latestBlogs[blogIndex];
                        return (
                          <article
                            key={blog.id_blog}
                            className="group rounded-2xl overflow-hidden border border-white/15 bg-white/10 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.18)] hover:bg-white/15 transition flex flex-col h-full min-h-105"
                          >
                            <div className="relative h-44 w-full shrink-0">
                              <Image
                                src={getImageUrl(blog.header_img || null)}
                                alt={blog.title}
                                fill
                                className="object-cover"
                                sizes="100vw"
                                unoptimized
                              />
                              <div className="absolute inset-0 bg-linear-to-t from-black/35 via-black/0 to-black/0" />
                            </div>

                            <div className="p-5 flex flex-col flex-1">
                              <p className="text-xs text-black/70 mb-2">
                                {blog.published_at
                                  ? new Date(blog.published_at).toLocaleDateString(
                                    language === "id" ? "id-ID" : "en-US",
                                    { year: "numeric", month: "long", day: "numeric" }
                                  )
                                  : ""}
                              </p>

                              <h4 className="font-semibold text-lg leading-snug mb-2 line-clamp-2">
                                {blog.title}
                              </h4>

                              <p className="text-sm text-black/80 mb-4 line-clamp-3">
                                {blog.abstract || ""}
                              </p>

                              <div className="mt-auto flex justify-end">
                                <Link
                                  href={`/blogs/${blog.id_blog}`}
                                  className="inline-flex items-center gap-2 text-sm font-semibold text-black hover:opacity-90 transition"
                                >
                                  Continue Reading
                                  <span className="translate-x-0 group-hover:translate-x-1 transition-transform">→</span>
                                </Link>
                              </div>
                            </div>
                          </article>
                        );
                      })()
                    ) : (
                      <div className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl h-64 animate-pulse" />
                    )}
                  </div>

                  <button
                    aria-label="Next blog"
                    onClick={blogNext}
                    className="p-2 rounded-md bg-white/10 hover:bg-white/20 text-[#0C2E6C]"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                  {latestBlogs && latestBlogs.length > 0 ? (
                    latestBlogs.map((blog) => (
                      <article
                        key={blog.id_blog}
                        className="group rounded-2xl overflow-hidden border border-white/15 bg-white/10 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.18)] hover:bg-white/15 transition flex flex-col h-full min-h-105"
                      >
                        {/* Image */}
                        <div className="relative h-44 w-full shrink-0">
                          <Image
                            src={getImageUrl(blog.header_img || null)}
                            alt={blog.title}
                            fill
                            className="object-cover"
                            sizes="(max-width: 640px) 100vw, 33vw"
                            unoptimized
                          />
                          {/* Gradient overlay for readability */}
                          <div className="absolute inset-0 bg-linear-to-t from-black/35 via-black/0 to-black/0" />
                        </div>

                        <div className="p-5 flex flex-col flex-1">
                          <p className="text-xs text-black/70 mb-2">
                            {blog.published_at
                              ? new Date(blog.published_at).toLocaleDateString(
                                language === "id" ? "id-ID" : "en-US",
                                { year: "numeric", month: "long", day: "numeric" }
                              )
                              : ""}
                          </p>

                          <h4 className="font-semibold text-lg leading-snug mb-2 line-clamp-2">
                            {blog.title}
                          </h4>

                          <p className="text-sm text-black/80 mb-4 line-clamp-3">
                            {blog.abstract || ""}
                          </p>

                          <div className="mt-auto flex justify-end">
                            <Link
                              href={`/blogs/${blog.id_blog}`}
                              className="inline-flex items-center gap-2 text-sm font-semibold text-black hover:opacity-90 transition"
                            >
                              Continue Reading
                              <span className="translate-x-0 group-hover:translate-x-1 transition-transform">→</span>
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))
                  ) : (
                    Array.from({ length: 3 }).map((_, i) => (
                      <div
                        key={`ph-${i}`}
                        className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl
                           h-64 animate-pulse"
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Get to Know More About GaneshaIT Section */}
        <section className="ganeshait-carousel-section">
          <div ref={ganeshaitRef} className="container">
            <div className="ganeshait-header">
              <h2
                className={`
                ganeshait-title reveal
                flex flex-col md:flex-row
                items-center justify-center
                gap-y-2 md:gap-x-3
                text-center
                ${ganeshaitInView ? "is-visible reveal-delay-100" : ""}
                ${heroLoaded && heroContent['hero-4'] ? 'opacity-100' : 'opacity-0'}
                transition-opacity duration-400
              `}
              >
                {heroContent['hero-4']?.content_title
                  ? (() => {
                    const title = heroContent['hero-4'].content_title;
                    const titleLower = title.toLowerCase();

                    if (titleLower.includes('ganeshait')) {
                      const parts = title.split(/ganeshait/i);
                      return (
                        <>
                          <span className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl md:whitespace-nowrap">
                            {parts[0]}
                          </span>
                          <span className="relative inline-block h-9 w-50 sm:h-7.5 sm:w-55 md:h-11 md:w-65 lg:h-13.75 lg:w-75 shrink-0 md:-translate-y-0.5 lg:-translate-y-1.75">
                            <Image src="/homepage/homepage-picture/ganeshait.svg"
                              alt="GaneshaIT"
                              fill
                              className="object-contain"
                              priority
                              sizes="(max-width: 640px) 200px, (max-width: 768px) 220px, (max-width: 1024px) 260px, 300px" />
                          </span>
                          {parts[1] && <span className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl">{parts[1]}</span>}
                        </>
                      );
                    }
                    return <span className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl">{title}</span>;
                  })()
                  : (fetchError ? (
                    <>
                      <span className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl md:whitespace-nowrap">
                        Get to Know More About
                      </span>
                      <span className="relative inline-block h-9 w-50 sm:h-7.5 sm:w-55 md:h-11 md:w-65 lg:h-13.75 lg:w-75 shrink-0 md:-translate-y-0.5 lg:-translate-y-1.75">
                        <Image src="/homepage/homepage-picture/ganeshait.svg"
                          alt="GaneshaIT"
                          fill
                          className="object-contain"
                          priority
                          sizes="(max-width: 640px) 200px, (max-width: 768px) 220px, (max-width: 1024px) 260px, 300px" />
                      </span>
                    </>
                  ) : '')
                }
              </h2>

              <p className={`ganeshait-description reveal ${ganeshaitInView ? 'is-visible reveal-delay-200' : ''} ${heroLoaded && heroContent['hero-4'] ? 'opacity-100' : 'opacity-0'} transition-opacity duration-400`}>
                {heroContent['hero-4']?.content_description || (fetchError ? 'Discover what makes GaneshaIT a trusted partner for enterprise technology solutions. From our world-class engineering team to our innovative approach, learn more about our values and commitment to excellence.' : '')}
              </p>
            </div>

            <div className={`ganeshait-carousel ${ganeshaitCards.length > 0 ? 'opacity-100' : 'opacity-0 h-0'} transition-all duration-400`}>
              <div className="ganeshait-carousel-container">
                {ganeshaitCards.map((card, index) => {
                  let position = index - ganeshaitCurrentIndex;

                  // Normalize position to be between -1 and 1 for circular carousel
                  if (position > 1) position -= ganeshaitCards.length;
                  if (position < -1) position += ganeshaitCards.length;

                  let translateX = 0;
                  let scale = 1;
                  let opacity = 1;
                  let zIndex = 1;
                  let pointerEvents: 'auto' | 'none' = 'auto';

                  const isMobile = windowWidth <= 1053;
                  const isSmallMobile = windowWidth <= 768;

                  // card sizes: use stable SSR defaults until mounted
                  let cardWidth = 500;
                  let cardHeight = 500;

                  if (windowWidth <= 480) { cardWidth = 260; cardHeight = 320; }
                  else if (isSmallMobile) { cardWidth = 280; cardHeight = 350; }
                  else if (isMobile) { cardWidth = 320; cardHeight = 400; }

                  // translate/scale rules: apply based on position
                  if (position === 0) {
                    translateX = 0;
                    scale = 1;
                    opacity = 1;
                    zIndex = 10;
                    pointerEvents = 'auto';
                  } else if (position === -1 && !isMobile) {
                    // Left preview card - only show on desktop
                    translateX = -400;
                    scale = 0.8;
                    opacity = 0.6;
                    zIndex = 5;
                    pointerEvents = 'none';
                  } else if (position === 1 && !isMobile) {
                    // Right preview card - only show on desktop
                    translateX = 400;
                    scale = 0.8;
                    opacity = 0.6;
                    zIndex = 5;
                    pointerEvents = 'none';
                  } else {
                    // Hidden cards (shouldn't happen with 3 cards, but keeping for safety)
                    translateX = position < 0 ? -800 : 800;
                    scale = 0.6;
                    opacity = 0;
                    zIndex = 1;
                    pointerEvents = 'none';
                  }

                  return (
                    <div
                      key={card.id_card}
                      className="ganeshait-card"
                      style={{
                        transform: `translateX(${translateX}px) scale(${scale})`,
                        opacity: opacity,
                        zIndex: zIndex,
                        pointerEvents: pointerEvents,
                        transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        marginLeft: `-${cardWidth / 2}px`,
                        marginTop: `-${cardHeight / 2}px`
                      }}
                      onClick={() => {
                        if (position !== 0) {
                          // If clicking on an inactive card, navigate to it
                          ganeshaitGoToSlide(index);
                        }
                      }}
                    >
                      {/* Blur overlay for inactive cards */}
                      {position !== 0 && (
                        <div className="ganeshait-card-blur-overlay"></div>
                      )}

                      <div className="ganeshait-card-background">
                        <div className="ganeshait-card-illustration">
                          <Image src={card.logo}
                            alt={card.card_title}
                            fill
                            className="object-contain"
                            sizes="(max-width: 480px) 23px, (max-width: 768px) 72px, (max-width: 1053px) 80px, 100px"
                            loading="lazy" />
                        </div>
                        <div className="ganeshait-card-content">
                          <h3 className="ganeshait-card-title">{card.card_title}</h3>
                          <p className="ganeshait-card-description">{card.card_description}</p>
                        </div>

                        {/* <div className="ganeshait-card-illustration">
                        <div className="ganeshait-card-icon">
                          <Image src={card.icon}
                            alt={card.title}
                            fill
                            className="object-contain"
                            sizes="(max-width: 480px) 23px, (max-width: 768px) 72px, (max-width: 1053px) 80px, 100px"
                           loading="lazy" />
                      </div> */}


                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Navigation arrows */}
              <button className="ganeshait-carousel-nav prev" onClick={ganeshaitPrevSlide}>
                <svg className="ganeshait-nav-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button className="ganeshait-carousel-nav next" onClick={ganeshaitNextSlide}>
                <svg className="ganeshait-nav-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />
      {/* Floating Chat Widget */}
      <ChatWidget />
    </div>
  );
}

